import type { MessageNoticeDTO, PayTemplateParams } from '@/types/notice'

// 消息中心支付通知展示规则：列表摘要和交易详情弹窗共用，逻辑对齐 H5 noticeCenter/components/translate.ts。
type Translate = (key: string, named?: Record<string, unknown>) => string
type PaymentNoticeLike = {
  templateType?: number
  templateParams?: PayTemplateParams
}

const depositTypes = [1, 3, 5]
const rejectTypes = [3, 4]
const successWithdrawTypes = [6, 2]

export const isPaymentNoticeReject = (item: Pick<MessageNoticeDTO, 'templateType'>) =>
  item.templateType !== undefined && rejectTypes.includes(item.templateType)

export const isPaymentNoticeWithdrawSuccess = (item: Pick<MessageNoticeDTO, 'templateType'>) =>
  item.templateType !== undefined && successWithdrawTypes.includes(item.templateType)

export const isPaymentNoticeDeposit = (item: Pick<MessageNoticeDTO, 'templateType'>) =>
  depositTypes.includes(item.templateType ?? 0)

export const getPaymentNoticeBank = (data: PaymentNoticeLike, t: Translate): string => {
  switch (data.templateParams?.title) {
    case 'MPAY_TRANSFER':
      return t('MPAY')
    case 'XPAY_TRANSFER':
      return t('XPAY')
    case 'MANUAL_IN':
      return t('trade_record_manual_in_text')
    case 'MANUAL_OUT':
      return t('trade_record_manual_out_text')
    case 'BANK_TRANSFER':
      return t('trade_record_bank_transfer_text')
    case 'DIGITAL_TRANSFER':
      return t('trade_record_digital_bank_text')
    case 'VIRTUAL_TRANSFER':
      return t('trade_record_virtual_transfer_text')
    case 'MANUAL_DEPOSIT':
      return t('trade_record_customer_transfer_text')
    case 'MANUAL_WITHDRAW':
      return t('trade_record_customer_transfer_text')
    case 'VIETTEL':
      return t('trade_record_vtpay_transfer_text')
    case 'ZALO':
      return t('trade_record_zalopay_transfer_text')
    case 'MOMO':
      return t('trade_record_momopay_transfer_text')
    default:
      return ''
  }
}

export const getPaymentNoticeType = (data: PaymentNoticeLike, t: Translate): string =>
  isPaymentNoticeDeposit(data) ? t('mine_card_deposit_text') : t('取款')

export const translatePaymentNoticeTitle = (data: PaymentNoticeLike, t: Translate): string =>
  t('pay_notice_transfer_text', {
    bank: getPaymentNoticeType(data, t),
    type: isPaymentNoticeReject(data) ? t('失败') : t('成功')
  })

export const translatePaymentNoticeContent = (data: PaymentNoticeLike, t: Translate): string =>
  t('notice_center_the_account_has_arrived_text', {
    bank: getPaymentNoticeBank(data, t),
    type: getPaymentNoticeType(data, t)
  })

export const translatePaymentNoticeOtherContent = (
  data: PaymentNoticeLike,
  t: Translate
): string => {
  if (data.templateType === 3) return t('trade_record_recharge_reject_text')
  if (data.templateType === 4) return t('trade_record_withdraw_audit_reject_text')
  if (data.templateType === 5) return t('trade_record_artificial_recharge_text')
  if (data.templateType === 6) return t('trade_record_artificial_withdraw_text')
  return ''
}

export const getPaymentNoticeAmountSign = (data: Pick<MessageNoticeDTO, 'templateType'>) =>
  isPaymentNoticeDeposit(data) ? '+' : '-'

export const getPaymentNoticeAmountTone = (data: Pick<MessageNoticeDTO, 'templateType'>) => {
  if (isPaymentNoticeReject(data)) return 'red'
  if (isPaymentNoticeWithdrawSuccess(data)) return 'white'
  return 'green'
}

export const getPaymentNoticeStatus = (
  data: Pick<MessageNoticeDTO, 'templateType'>,
  t: Translate
) =>
  isPaymentNoticeReject(data)
    ? { text: t('Unsuccessful'), tone: 'red' as const }
    : { text: t('Approved'), tone: 'green' as const }
